import { reactive, computed, ref } from 'vue'

const STORAGE_KEY = 'pomodoro_records'

// 番茄钟记录结构
class PomodoroRecord {
  constructor(duration = 25, note = '') {
    this.id = Date.now()
    this.startTime = new Date()
    this.endTime = null
    this.duration = duration // 分钟
    this.completed = false
    this.note = note
    this.tags = []
  }
}

// 存储的番茄钟数据
const pomodoroData = reactive({
  records: [],
  settings: {
    workDuration: 25,
    shortBreakDuration: 5,
    longBreakDuration: 15,
    sessionsBeforeLongBreak: 4
  }
})

// 从localStorage加载数据
function loadData() {
  try {
    const data = localStorage.getItem(STORAGE_KEY)
    if (data) {
      const parsed = JSON.parse(data)
      pomodoroData.records = parsed.records || []
      pomodoroData.settings = { ...pomodoroData.settings, ...parsed.settings }
    }
  } catch (error) {
    console.error('Failed to load pomodoro data:', error)
  }
}

// 保存数据到localStorage
function saveData() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      records: pomodoroData.records,
      settings: pomodoroData.settings
    }))
  } catch (error) {
    console.error('Failed to save pomodoro data:', error)
  }
}

// 初始化加载
loadData()

export function usePomodoro() {
  const currentPomodoro = ref(null)
  const isRunning = ref(false)
  const timeLeft = ref(0) // 秒
  let timer = null

  // 使用 success.mp3 文件作为提示音
  const playCompletionSound = () => {
    try {
      const audio = new Audio('/success.mp3')
      audio.volume = 0.8
      const playPromise = audio.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => console.log('✓ Completion sound played'))
          .catch(err => console.warn('Sound play failed:', err))
      }
    } catch (err) {
      console.error('Audio play error:', err)
      // 降级方案：使用 Web Audio API
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)()
        const now = audioContext.currentTime
        const osc1 = audioContext.createOscillator()
        const osc2 = audioContext.createOscillator()
        const gain = audioContext.createGain()
        
        osc1.frequency.value = 800
        osc2.frequency.value = 1000
        gain.gain.setValueAtTime(0.3, now)
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5)
        
        osc1.connect(gain)
        osc2.connect(gain)
        gain.connect(audioContext.destination)
        
        osc1.start(now)
        osc2.start(now)
        osc1.stop(now + 0.5)
        osc2.stop(now + 0.5)
        console.log('✓ Fallback Web Audio API sound played')
      } catch (e) {
        console.warn('Fallback audio failed:', e)
      }
    }
  }

  const finalize = ({ save = false, useOriginal = false }) => {
    if (save && currentPomodoro.value) {
      const elapsedSec = currentPomodoro.value.duration * 60 - timeLeft.value
      const elapsedMinutes = Math.max(1, Math.round(elapsedSec / 60))
      const record = {
        ...currentPomodoro.value,
        endTime: new Date(),
        completed: true,
        actualMinutes: elapsedMinutes,
        duration: useOriginal ? currentPomodoro.value.duration : elapsedMinutes
      }
      pomodoroData.records.push(record)
      saveData()
    }

    if (timer) {
      clearInterval(timer)
      timer = null
    }
    isRunning.value = false
    currentPomodoro.value = null
    timeLeft.value = 0
  }

  // 停止番茄钟（save=true时保存当前进度为一次完成）
  const stopPomodoro = (save = false) => {
    finalize({ save, useOriginal: false })
  }

  // 开始番茄钟
  const startPomodoro = (duration = pomodoroData.settings.workDuration, note = '') => {
    if (isRunning.value) return

    currentPomodoro.value = new PomodoroRecord(duration, note)
    timeLeft.value = duration * 60
    isRunning.value = true

    timer = setInterval(() => {
      if (timeLeft.value > 0) {
        timeLeft.value--
      } else {
        completePomodoro()
      }
    }, 1000)
  }

  // 完成番茄钟
  const completePomodoro = () => {
    if (!currentPomodoro.value) return

    finalize({ save: true, useOriginal: true })

    // 播放提示音
    playCompletionSound()
    
    // 显示通知
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        try {
          new Notification('番茄钟完成！', {
            body: '恭喜完成一个番茄钟，休息一下吧！',
            icon: '/favicon.ico',
            tag: 'pomodoro-complete',
            requireInteraction: true
          })
          console.log('✓ Notification sent')
        } catch (err) {
          console.error('Notification error:', err)
        }
      }
    }
  }

  // 暂停/继续
  const togglePause = () => {
    if (isRunning.value && timer) {
      clearInterval(timer)
      timer = null
      isRunning.value = false
    } else if (!isRunning.value && currentPomodoro.value) {
      isRunning.value = true
      timer = setInterval(() => {
        if (timeLeft.value > 0) {
          timeLeft.value--
        } else {
          completePomodoro()
        }
      }, 1000)
    }
  }

  // 应用数据 localStorage key 列表
  const APP_KEYS = ['clock_settings', 'clock_sound_enabled', 'locale', 'pomodoro_records']

  // 导出全部应用数据
  const exportData = () => {
    const allData = { exportTime: new Date().toISOString() }
    for (const key of APP_KEYS) {
      const val = localStorage.getItem(key)
      if (val !== null) {
        try { allData[key] = JSON.parse(val) } catch { allData[key] = val }
      }
    }
    const blob = new Blob([JSON.stringify(allData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `focus-timer-data-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  // 导入全部应用数据
  const importData = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result)
          // 兼容旧版只含 pomodoro 的导出文件
          if (data.records) {
            pomodoroData.records = [...pomodoroData.records, ...data.records]
            if (data.settings) {
              pomodoroData.settings = { ...pomodoroData.settings, ...data.settings }
            }
            saveData()
          }
          // 恢复其他设置
          for (const key of APP_KEYS) {
            if (data[key] !== undefined) {
              const val = typeof data[key] === 'string' ? data[key] : JSON.stringify(data[key])
              localStorage.setItem(key, val)
            }
          }
          // 刷新内存中的时钟设置和语言
          if (data.clock_settings) {
            Object.assign(pomodoroData.settings, pomodoroData.settings) // 保持不变
          }
          resolve(data)
        } catch (error) {
          reject(error)
        }
      }
      reader.onerror = reject
      reader.readAsText(file)
    })
  }

  // 统计数据
  const stats = computed(() => {
    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const weekStart = new Date(todayStart)
    weekStart.setDate(weekStart.getDate() - weekStart.getDay())
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)

    const todayRecords = pomodoroData.records.filter(r => 
      r.completed && new Date(r.startTime) >= todayStart
    )
    const weekRecords = pomodoroData.records.filter(r => 
      r.completed && new Date(r.startTime) >= weekStart
    )
    const monthRecords = pomodoroData.records.filter(r => 
      r.completed && new Date(r.startTime) >= monthStart
    )
    const allRecords = pomodoroData.records.filter(r => r.completed)

    return {
      today: {
        count: todayRecords.length,
        duration: todayRecords.reduce((sum, r) => sum + r.duration, 0)
      },
      week: {
        count: weekRecords.length,
        duration: weekRecords.reduce((sum, r) => sum + r.duration, 0)
      },
      month: {
        count: monthRecords.length,
        duration: monthRecords.reduce((sum, r) => sum + r.duration, 0)
      },
      total: {
        count: allRecords.length,
        duration: allRecords.reduce((sum, r) => sum + r.duration, 0)
      }
    }
  })

  // 格式化时间显示
  const formatTime = computed(() => {
    const minutes = Math.floor(timeLeft.value / 60)
    const seconds = timeLeft.value % 60
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  })

  return {
    // 状态
    currentPomodoro,
    isRunning,
    timeLeft,
    formatTime,
    records: computed(() => pomodoroData.records),
    settings: pomodoroData.settings,
    stats,

    // 方法
    startPomodoro,
    stopPomodoro,
    togglePause,
    completePomodoro,
    exportData,
    importData,
    saveData,
    
    // 更新并保存设置
    updateSettings: (newSettings) => {
      Object.assign(pomodoroData.settings, newSettings)
      saveData()
    }
  }
}
